// Presentation only: never change transport privacy or infer delivery status.
// Keep the existing account boundary of the signed WAHA display integration.
export function getWhatsAppHistoryMeta(message) {
  const attrs = message.contentAttributes ?? message.content_attributes ?? {};
  const meta = attrs.bcwHistoryV1 || attrs.bcw_history_v1;
  const inboxId = message.inboxId ?? message.inbox_id;
  const createdAt = message.createdAt ?? message.created_at;
  if (!meta || meta.origin !== 'waha' || attrs.deleted) return null;
  if ((meta.inboxId ?? meta.inbox_id) !== inboxId) return null;
  if ((meta.accountId ?? meta.account_id) !== 1) return null;
  if (!/^[a-f0-9]{64}$/.test(meta.signature || '')) return null;
  if (
    !Number.isSafeInteger(createdAt) ||
    !Number.isSafeInteger(meta.timestamp) ||
    meta.timestamp < 1230768000 ||
    meta.timestamp > createdAt
  ) {
    return null;
  }
  return meta;
}

// bcw_history_origin is a read-only server projection. The serializer removes
// supplied values and rebuilds it after verifying the message's signed source.
// Never use delivery status, sender names or transport privacy as provenance.
export function getWhatsAppOrigin(attributes = {}, history = null) {
  if (attributes.deleted || attributes.bcw_revocation_v1 || attributes.bcwRevocationV1) return null;
  if (history?.outgoing === true && history.mode === 'phone_echo') return 'phone';
  const source = attributes.bcwHistoryOrigin ?? attributes.bcw_history_origin;
  if (source?.version === 1 && source.origin === 'history') return 'history';
  return history?.outgoing === true ? 'history' : null;
}

export function getWhatsAppPresentation(message) {
  const attributes = message.contentAttributes ?? message.content_attributes ?? {};
  const history = getWhatsAppHistoryMeta(message);
  const outgoing = (message.messageType ?? message.message_type) === 1;
  const origin = outgoing ? getWhatsAppOrigin(attributes, history) : null;
  return {
    history,
    origin,
    isSyncedOutgoing: origin !== null,
    isPrivateNote: Boolean(message.private) && origin === null,
  };
}

export function isWhatsAppSyncedOutgoing(message) {
  return getWhatsAppPresentation(message).isSyncedOutgoing;
}

export function isWhatsAppPrivateNote(message) {
  return getWhatsAppPresentation(message).isPrivateNote;
}

// Strip only the pinned WAHA phone-echo wrapper from the display copy.
// Raw content, provenance and transport flags remain unchanged.
export function getWhatsAppDisplayContent(message) {
  const content = message.content;
  if (typeof content !== 'string' || !isWhatsAppSyncedOutgoing(message)) {
    return content;
  }
  if (getWhatsAppHistoryMeta(message)?.mode !== 'phone_echo') return content;
  // Media-only echoes can contain just the wrapper and one trailing newline.
  if (/^\*📱 Sent from WhatsApp\*[\t \r\n]*$/.test(content)) return '';
  return content.replace(/^\*📱 Sent from WhatsApp\*\r?\n\r?\n/, '');
}
